import { TutorialHandler } from './handlers/TutorialHandler';
import { getTutorialServices } from './services/TutorialServices';
import { DuoInviteTutorial } from '../tutorial/handlers/10-DuoInviteTutorial';
import { DuoInviteReceivedTutorial } from '../tutorial/handlers/14-DuoInviteReceivedTutorial';
import { DecorationTutorial } from '../tutorial/handlers/17-DecorationTutorial';
import { DaycareTutorial } from '../tutorial/handlers/18-DaycareTutorial';
import { CatalogTutorial } from '../tutorial/handlers/23-CatalogTutorial';
import { GrowthCapsuleTutorial } from '../tutorial/handlers/25-GrowthCapsuleTutorial';
import { EncyclopediaTutorial } from '../tutorial/handlers/29-EncyclopediaTutorial';
import { SlotsTutorial } from '../tutorial/handlers/33-SlotsTutorial';
import { Match2Tutorial } from '../tutorial/handlers/38-Match2Tutorial';
import { BumBumTutorial } from '../tutorial/handlers/40-BumBumTutorial';
import { TeamsTutorial } from '../tutorial/handlers/45-TeamsTutorial';
import { FriendReferralTutorial } from '../tutorial/handlers/5-FriendReferralTutorial';
import { ProfileTutorial } from '../tutorial/handlers/6-ProfileTutorial';
import { Match3Tutorial } from '../tutorial/handlers/7-Match3Tutorial';
import { VisitFriendTutorial } from '../tutorial/handlers/8-VisitFriendTutorial';
import { AdultTutorial } from '../tutorial/handlers/9-AdultTutorial';
import { PuzzleCoinsBasicTutorial } from '../puzzlecoins/tutorials/1-PuzzleCoinsBasicTutorial';
import { PuzzleCoinsCareTutorial } from '../puzzlecoins/tutorials/2-PuzzleCoinsCareTutorial';

// All supported tutorial events.
export type TutorialEvent =
  | 'gameStarted'
  | 'careMenuOpened'
  | 'friendsMenuOpened'
  | 'careMenuClosed'
  | 'carePopupOpened'
  | 'carePopupClosed'
  | 'careActionCompleted'
  | 'conversationStarted'
  | 'conversationFinished'
  | 'friendReferralPopupOpened'
  | 'profilePopupOpened'
  | 'match3Started'
  | 'visitPopupOpened'
  | 'duoInviteSequenceStarted'
  | 'duoInvitationReceived'
  | 'petEvolved'
  | 'levelIncreased'
  | 'decorationInProgress'
  | 'decorationFinished'
  | 'decorationRewardClaimed'
  | 'competitiveMinigameStarted'
  | 'bonusItemReceived'
  | 'catalogOpen'
  | 'mainMenuOpened'
  | 'encyclopediaOpenedAfterEvolution'
  | 'growthCapsulePurchase'
  | 'slotsUnlocked'
  | 'bubbleSlotUnlocked'
  | 'throwBuruBuruUnlocked'
  | 'match2Started'
  | 'match2LevelCompleted'
  | 'match2LevelFailed'
  | 'decorationProgressionUnlocked'
  | 'slotMachineSpun'
  | 'bumbumStarted'
  | 'transformUnlocked'
  | 'newCostumeCollected'
  | 'careConfirmPopupOpened'
  | 'costumeDetailsOpened'
  | 'teamPopupOpened'
  | 'teamDiscoveryOpened'
  | 'tapPet'
  | 'match2PreGamePopupOpened'
  | 'inviteChallengePresented'
  | 'minigameUnlocked'
  | 'nativeRewardsClaimed'
  | 'careItemConfirmed'
  | 'puzzleCoinsBasicStepComplete';

export class TutorialController {
  /**
   * Services.
   */
  public services = getTutorialServices();

  /**
   * List of tutorial handlers.
   */
  private handlers: TutorialHandler[];

  constructor() {
    this.handlers = [
      new PuzzleCoinsBasicTutorial(),
      new PuzzleCoinsCareTutorial(),
      new FriendReferralTutorial(),
      new ProfileTutorial(),
      new Match3Tutorial(),
      new VisitFriendTutorial(),
      new AdultTutorial(),
      new DuoInviteTutorial(),
      new DuoInviteReceivedTutorial(),
      new DecorationTutorial(),
      new DaycareTutorial(),
      new CatalogTutorial(),
      new GrowthCapsuleTutorial(),
      new EncyclopediaTutorial(),
      new SlotsTutorial(),
      new Match2Tutorial(),
      new BumBumTutorial(),
      new TeamsTutorial(),
    ];
  }

  /**
   * Handles an event, starting a tutorial if necessary.
   * @param event - The event to handle.
   * @returns - True if a tutorial was started, false otherwise.
   */
  public async handleEvent<T>(
    event: TutorialEvent,
    data?: T,
  ): Promise<boolean> {
    // Filter out handlers that are already complete.
    const remainingHandlers = [];
    for (let i = 0; i < this.handlers.length; i++) {
      const handler = this.handlers[i];

      if (!handler.isTutorialComplete()) {
        remainingHandlers.push(handler);
      }
    }
    this.handlers = remainingHandlers;

    // Execute all handlers.
    let executed = false;
    for (const handler of this.handlers) {
      handler.services = this.services;
      executed ||= await handler.handleEvent<T>(event, data);
    }

    return executed;
  }
}

let tutorialController: TutorialController;
export function getTutorialController() {
  if (!tutorialController) {
    tutorialController = new TutorialController();

    if (process.env.IS_DEVELOPMENT) {
      (
        window as { TutorialController?: TutorialController }
      ).TutorialController = tutorialController;
    }
  }

  return tutorialController;
}
